/**
 * The ad's page (`/ads/:id`; docs/SPEC.md §6.1, §14.1; P28-T09): one page
 * whose stage follows the ad's state (`brief → building → ready`,
 * `failed → building`).
 *
 * - **brief**: `ad/BriefStage.tsx`: what was said, the conversation (§5) and
 *   the brief card (§5.5). The final brief ends it, with no button between.
 * - **building** and **ready**: `ad/FilmPage.tsx`: the player opening on the
 *   first scene as soon as it is built, the scene strip filling in, the whole
 *   film once every scene is ready; the conversation beside it from version
 *   1's writing on; the cast panel (`ad/CastPanel.tsx`, §6.3); the brief as
 *   a fold; versions and ways out. Arriving from the design studio's **Use
 *   in {ad}** (§6.4), the page says what happens to the film.
 * - **failed**: the ad's sentence and **Try again** (§6.2), over the brief
 *   stage.
 *
 * Everything comes from one query (`GET /v1/ads/{id}`), kept current by the
 * ad's socket; while the socket is not open the page polls.
 */
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  type AdDetail,
  TITLE_MAX,
  adKey,
  useAd,
  useRenameAd,
  useRetry,
} from "../api/ads";
import {
  ApiError,
  newIdempotencyKey,
  priceAsk,
  shortfallOf,
} from "../api/client";
import { CREDITS, sayCredits } from "../copy/credits";
import { PriceConfirm, TopUp } from "../ui/Credits";
import { formatDate } from "../ui/format";
import { BriefStage } from "../ad/BriefStage";
import { CastPanel } from "../ad/CastPanel";
import { CAST } from "../copy/cast";
import type { FromStudio } from "../api/models";
import { type AdBus, type AdListener, FilmPage } from "../ad/FilmPage";
import { useAdSocket } from "../ad/useAdSocket";
import { ASPECT_NAMES, FILM, PAGE, STATE_LABELS, say } from "../copy/brief";
import { sentenceFor } from "../copy/errors";
import { useDocumentTitle } from "../ui/useDocumentTitle";
import {
  Button,
  Card,
  Input,
  PageHeader,
  Pill,
  type PillTone,
  Skeleton,
} from "../ui";

export const POLL_MS = 5_000;

const STATE_TONES: Record<AdDetail["state"], PillTone> = {
  brief: "purple",
  building: "warning",
  ready: "success",
  failed: "danger",
};

/** The name under the heading, editable in place (§14.1.1): Enter or leaving
 *  the field saves, Esc cancels. */
function AdName({ ad }: { ad: AdDetail }) {
  const rename = useRenameAd(ad.id);
  const [draft, setDraft] = useState<string | null>(null);
  const dropped = useRef(false);
  const save = () => {
    if (dropped.current || draft === null) return;
    const name = draft.trim();
    setDraft(null);
    if (name && name !== ad.title) rename.mutate(name);
  };
  if (draft !== null) {
    return (
      <Input
        aria-label={PAGE.nameLabel}
        autoFocus
        value={draft}
        maxLength={TITLE_MAX}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            save();
          } else if (e.key === "Escape") {
            dropped.current = true;
            setDraft(null);
          }
        }}
      />
    );
  }
  return (
    <span className="flex flex-col items-start gap-1">
      <span>{ad.title}</span>
      <button
        type="button"
        className="inline-flex min-h-[44px] items-center text-sm font-normal underline md:min-h-0"
        disabled={rename.isPending}
        onClick={() => {
          dropped.current = false;
          setDraft(ad.title);
        }}
      >
        {PAGE.editName}
      </button>
      {rename.error ? (
        <span role="alert" className="text-sm font-normal text-slate-700">
          {sentenceFor(rename.error)}
        </span>
      ) : null}
    </span>
  );
}

/** The ad's details, the page's menu (§15.1, P28-T08): when it was made and
 *  what it has cost so far — its total lives here and on its Media folder,
 *  not under each answer. Opening it reads the ad again, so the total is
 *  today's. */
function AdInfo({ ad }: { ad: AdDetail }) {
  const queryClient = useQueryClient();
  const spent = ad.credits_captured ?? 0;
  return (
    <details className="group md:text-right" data-testid="ad-details">
      <summary
        className="inline-flex min-h-[44px] cursor-pointer list-none items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 hover:border-slate-300 md:min-h-[36px] [&::-webkit-details-marker]:hidden"
        onClick={(e) => {
          if (!(e.currentTarget.parentElement as HTMLDetailsElement).open) {
            void queryClient.invalidateQueries({ queryKey: adKey(ad.id) });
          }
        }}
      >
        {CREDITS.details}
        <span aria-hidden="true" className="text-xs group-open:rotate-180">
          ▾
        </span>
      </summary>
      {/* in the flow, under the button: it stays on the screen whether the
          heading puts the button on the right or, on a phone, the left */}
      <div className="mt-1 flex max-w-[calc(100vw-2rem)] flex-col gap-1 rounded-lg text-left border border-slate-200 bg-white p-3 text-sm text-slate-700 shadow-sm">
        <p className="m-0 font-semibold text-slate-900" data-testid="ad-total">
          {spent > 0
            ? sayCredits(CREDITS.adTotal, { credits: spent })
            : CREDITS.adTotalNone}
        </p>
        <p className="m-0 text-slate-500">
          {sayCredits(CREDITS.created, { date: formatDate(ad.created_at) })}
        </p>
      </div>
    </details>
  );
}

/** Arriving from the design studio's **Use in {ad}** (§6.4): the sentence
 *  when the thing left the film, else what happens next. */
export function FromStudioNote() {
  const used = (useLocation().state as FromStudio | null)?.usedInAd;
  if (!used) return null;
  const sentence =
    used.sentence ?? (used.publishing ? CAST.publishing : CAST.used);
  return (
    <Card
      role="status"
      className="border-sky-200 bg-sky-50 p-4 text-sm text-slate-800"
      data-testid="from-studio"
    >
      {sentence}
    </Card>
  );
}

/** Failed (§6.2): the ad's sentence and Try again. */
function Failed({ ad }: { ad: AdDetail }) {
  const retry = useRetry(ad.id);
  const key = useRef(newIdempotencyKey());
  // §15.1: writing the film again waits for a yes at or above
  // `PRICE_CONFIRM_CREDITS`; below the estimate it is the block
  const [priced, setPriced] = useState<number | null>(null);
  const tryAgain = (confirm = false) => {
    setPriced(null);
    retry.mutate(
      { idempotencyKey: key.current, confirm },
      {
        onSuccess: (made) => {
          const ask = priceAsk(made);
          if (ask) setPriced(ask.estimate);
        },
        onSettled: () => {
          key.current = newIdempotencyKey();
        },
      },
    );
  };
  const short = shortfallOf(retry.error);
  return (
    <Card
      className="flex flex-col items-start gap-3 p-5"
      data-testid="failed"
      role="alert"
    >
      <p className="m-0 text-base font-semibold text-slate-900">
        {FILM.failed}
      </p>
      {ad.sentence ? (
        <p className="m-0 text-sm text-slate-700">{ad.sentence}</p>
      ) : null}
      <Button
        type="button"
        variant="primary"
        disabled={retry.isPending}
        onClick={() => tryAgain()}
      >
        {retry.isPending ? FILM.trying : FILM.tryAgain}
      </Button>
      {priced !== null ? (
        <PriceConfirm
          what="retry"
          credits={priced}
          busy={retry.isPending}
          onYes={() => tryAgain(true)}
          onNo={() => setPriced(null)}
        />
      ) : null}
      {short ? (
        <TopUp shortfall={short} />
      ) : retry.error ? (
        <p className="m-0 text-sm text-slate-700">{sentenceFor(retry.error)}</p>
      ) : null}
    </Card>
  );
}

function Page({
  ad,
  socket,
  bus,
}: {
  ad: AdDetail;
  socket: string;
  bus: AdBus;
}) {
  useDocumentTitle(ad.title);
  const state = ad.state;
  const brief = ad.brief_state.brief ?? ad.brief;
  const aspect = brief?.aspect ?? ad.options.aspect ?? "9:16";
  const options = [
    `${brief?.length_s ?? ad.options.length_s ?? 15} s`,
    ASPECT_NAMES[aspect] ?? aspect,
  ].join(" · ");

  return (
    <>
      <PageHeader
        eyebrow={
          <span className="inline-flex items-center gap-2">
            <Link to="/ads" className="no-underline">
              {PAGE.backToAds}
            </Link>
            <span aria-hidden="true">/</span>
            {PAGE.eyebrow.replace(/^— /, "")}
          </span>
        }
        title={<AdName ad={ad} />}
        right={<AdInfo ad={ad} />}
        meta={
          <span className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
            <Pill size="sm" tone={STATE_TONES[state]} data-testid="ad-state">
              {STATE_LABELS[state]}
            </Pill>
            <span>{options}</span>
            <span aria-hidden="true">·</span>
            <span data-testid="kit-line">
              {ad.brand_kit_name
                ? say(PAGE.brandKit, { name: ad.brand_kit_name })
                : PAGE.noBrandKit}
            </span>
            {socket === "reconnecting" ? (
              <span className="text-xs text-slate-400">
                {PAGE.reconnecting}
              </span>
            ) : null}
          </span>
        }
      />

      <div className="flex flex-col gap-6">
        <FromStudioNote />
        {state === "failed" ? <Failed ad={ad} /> : null}
        {state === "building" || state === "ready" ? (
          <FilmPage ad={ad} bus={bus} cast={<CastPanel ad={ad} />} />
        ) : (
          <BriefStage ad={ad} />
        )}
      </div>
    </>
  );
}

export function AdPage() {
  const { id = "" } = useParams();
  // the socket's events, also handed to the film page (an instant change's
  // patches go to the player)
  const listeners = useRef(new Set<AdListener>());
  const bus = useMemo<AdBus>(
    () => ({
      add: (listener) => {
        listeners.current.add(listener);
        return () => {
          listeners.current.delete(listener);
        };
      },
    }),
    [],
  );
  const socket = useAdSocket(id, true, (event) => {
    for (const listener of listeners.current) listener(event);
  });
  const ad = useAd(id, socket === "open" ? false : POLL_MS);
  if (ad.isPending) {
    return (
      <Card aria-busy="true" className="flex flex-col gap-3 p-6">
        <Skeleton height={22} width="40%" />
        <Skeleton height={14} />
        <Skeleton height={14} width="80%" />
      </Card>
    );
  }
  if (ad.isError || !ad.data) {
    const gone =
      ad.error instanceof ApiError &&
      (ad.error.status === 404 || ad.error.code === "not_found");
    return (
      <Card role="alert" className="flex flex-col items-start gap-3 p-6">
        <p className="m-0 text-slate-700">
          {gone ? PAGE.notFound : sentenceFor(ad.error)}
        </p>
        <Button as={Link} to="/ads" variant="secondary">
          {PAGE.backToAds}
        </Button>
      </Card>
    );
  }
  return <Page ad={ad.data} socket={socket} bus={bus} />;
}
